/**
 * Dashboard Application Module
 *
 * Service and hooks for dashboard statistics
 */

import { useQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import { tokenStorage } from '@/lib/api'

// =============================================================================
// Types
// =============================================================================

export interface DashboardStats {
  totalPatients: number
  totalStudies: number
  totalReports: number
  signedReportsToday: number
  pendingWorklist: number
  overdueWorklist: number
  unassignedWorklist: number
}

export interface ModalityDistribution {
  modality: string
  count: number
  percentage: number
}

export interface StudyTimelineItem {
  date: string
  count: number
}

export interface RadiologistProductivity {
  radiologistId: string
  radiologistEmail: string
  radiologistName: string
  completedToday: number
  completedWeek: number
  completedMonth: number
  avgTatHours: number | null
}

export interface TatMetrics {
  avgTatHours: number
  medianTatHours: number
  minTatHours: number
  maxTatHours: number
  percentile95Hours: number
}

/**
 * Personal stats (role-specific)
 * Fields vary by role - check which are defined
 */
export interface MyStats {
  role: string
  // RADIOLOGIST
  completedToday?: number
  completedWeek?: number
  completedMonth?: number
  avgTatHours?: number | null
  pendingCount?: number
  inProgressCount?: number
  overdueCount?: number
  // TRANSCRIPTOR
  transcribedToday?: number
  transcribedWeek?: number
  transcribedMonth?: number
  pendingQueue?: number
  // TECHNICIAN
  uploadedToday?: number
  uploadedWeek?: number
  uploadedMonth?: number
  assignedToday?: number
  // DOCTOR
  patientsTotal?: number
  studiesRequested?: number
  reportsAvailable?: number
}

/**
 * Personal productivity timeline item
 */
export interface ProductivityTimelineItem {
  date: string
  count: number
}

// =============================================================================
// Query Keys
// =============================================================================

const QUERY_KEYS = {
  // Institution-filtered (existing)
  stats: ['dashboard', 'stats'],
  studiesByModality: ['dashboard', 'studies-by-modality'],
  studiesTimeline: (days: number) => ['dashboard', 'studies-timeline', days],
  radiologistProductivity: ['dashboard', 'radiologist-productivity'],
  tatMetrics: ['dashboard', 'tat-metrics'],
  // Personal (user-specific)
  myStats: ['dashboard', 'my-stats'],
  myStudiesByModality: ['dashboard', 'my-studies-by-modality'],
  myProductivityTimeline: (days: number) => ['dashboard', 'my-productivity-timeline', days],
  // Global (SUPER_ADMIN only)
  globalStats: ['dashboard', 'global', 'stats'],
  globalStudiesByModality: ['dashboard', 'global', 'studies-by-modality'],
  globalStudiesTimeline: (days: number) => ['dashboard', 'global', 'studies-timeline', days],
  globalProductivity: ['dashboard', 'global', 'productivity'],
  globalTatMetrics: ['dashboard', 'global', 'tat-metrics'],
}

// =============================================================================
// API Functions
// =============================================================================

export async function fetchDashboardStats(): Promise<{ stats: DashboardStats }> {
  return get<{ stats: DashboardStats }>(API_ROUTES.DASHBOARD.STATS)
}

export async function fetchStudiesByModality(): Promise<{
  distribution: ModalityDistribution[]
  total: number
}> {
  return get<{ distribution: ModalityDistribution[]; total: number }>(
    API_ROUTES.DASHBOARD.STUDIES_BY_MODALITY
  )
}

export async function fetchStudiesTimeline(days = 30): Promise<{
  timeline: StudyTimelineItem[]
  days: number
}> {
  return get<{ timeline: StudyTimelineItem[]; days: number }>(
    `${API_ROUTES.DASHBOARD.STUDIES_TIMELINE}?days=${days}`
  )
}

export async function fetchRadiologistProductivity(): Promise<{
  productivity: RadiologistProductivity[]
}> {
  return get<{ productivity: RadiologistProductivity[] }>(
    API_ROUTES.DASHBOARD.RADIOLOGIST_PRODUCTIVITY
  )
}

export async function fetchTatMetrics(): Promise<{ metrics: TatMetrics }> {
  return get<{ metrics: TatMetrics }>(API_ROUTES.DASHBOARD.TAT_METRICS)
}

// =============================================================================
// Personal API Functions (user-specific)
// =============================================================================

export async function fetchMyStats(): Promise<{ stats: MyStats }> {
  return get<{ stats: MyStats }>(API_ROUTES.DASHBOARD.MY_STATS)
}

export async function fetchMyStudiesByModality(): Promise<{
  distribution: ModalityDistribution[]
  total: number
}> {
  return get<{ distribution: ModalityDistribution[]; total: number }>(
    API_ROUTES.DASHBOARD.MY_STUDIES_BY_MODALITY
  )
}

export async function fetchMyProductivityTimeline(days = 30): Promise<{
  timeline: ProductivityTimelineItem[]
  days: number
}> {
  return get<{ timeline: ProductivityTimelineItem[]; days: number }>(
    `${API_ROUTES.DASHBOARD.MY_PRODUCTIVITY_TIMELINE}?days=${days}`
  )
}

// =============================================================================
// Global API Functions (SUPER_ADMIN only)
// =============================================================================

export async function fetchGlobalStats(): Promise<{ stats: DashboardStats }> {
  return get<{ stats: DashboardStats }>(API_ROUTES.DASHBOARD.GLOBAL.STATS)
}

export async function fetchGlobalStudiesByModality(): Promise<{
  distribution: ModalityDistribution[]
  total: number
}> {
  return get<{ distribution: ModalityDistribution[]; total: number }>(
    API_ROUTES.DASHBOARD.GLOBAL.STUDIES_BY_MODALITY
  )
}

export async function fetchGlobalStudiesTimeline(days = 30): Promise<{
  timeline: StudyTimelineItem[]
  days: number
}> {
  return get<{ timeline: StudyTimelineItem[]; days: number }>(
    `${API_ROUTES.DASHBOARD.GLOBAL.STUDIES_TIMELINE}?days=${days}`
  )
}

export async function fetchGlobalRadiologistProductivity(): Promise<{
  productivity: RadiologistProductivity[]
}> {
  return get<{ productivity: RadiologistProductivity[] }>(
    API_ROUTES.DASHBOARD.GLOBAL.PRODUCTIVITY
  )
}

export async function fetchGlobalTatMetrics(): Promise<{ metrics: TatMetrics }> {
  return get<{ metrics: TatMetrics }>(API_ROUTES.DASHBOARD.GLOBAL.TAT_METRICS)
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch dashboard stats
 */
export function useDashboardStats() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.stats,
    queryFn: fetchDashboardStats,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch studies by modality distribution
 */
export function useStudiesByModality() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.studiesByModality,
    queryFn: fetchStudiesByModality,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch studies timeline
 */
export function useStudiesTimeline(days = 30) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.studiesTimeline(days),
    queryFn: () => fetchStudiesTimeline(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch radiologist productivity
 */
export function useRadiologistProductivity() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.radiologistProductivity,
    queryFn: fetchRadiologistProductivity,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch TAT metrics
 */
export function useTatMetrics() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.tatMetrics,
    queryFn: fetchTatMetrics,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

// =============================================================================
// Personal Hooks (user-specific)
// =============================================================================

/**
 * Hook to fetch personal stats (role-specific)
 */
export function useMyStats() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.myStats,
    queryFn: fetchMyStats,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch personal studies by modality
 */
export function useMyStudiesByModality() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.myStudiesByModality,
    queryFn: fetchMyStudiesByModality,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch personal productivity timeline
 */
export function useMyProductivityTimeline(days = 30) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.myProductivityTimeline(days),
    queryFn: () => fetchMyProductivityTimeline(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

// =============================================================================
// Global Hooks (SUPER_ADMIN only)
// =============================================================================

/**
 * Hook to fetch global stats (all institutions)
 */
export function useGlobalStats() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.globalStats,
    queryFn: fetchGlobalStats,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch global studies by modality
 */
export function useGlobalStudiesByModality() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.globalStudiesByModality,
    queryFn: fetchGlobalStudiesByModality,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch global studies timeline
 */
export function useGlobalStudiesTimeline(days = 30) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.globalStudiesTimeline(days),
    queryFn: () => fetchGlobalStudiesTimeline(days),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch global radiologist productivity
 */
export function useGlobalRadiologistProductivity() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.globalProductivity,
    queryFn: fetchGlobalRadiologistProductivity,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}

/**
 * Hook to fetch global TAT metrics
 */
export function useGlobalTatMetrics() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: QUERY_KEYS.globalTatMetrics,
    queryFn: fetchGlobalTatMetrics,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: hasToken,
  })
}
